import { useState, useRef, useEffect } from "react";
import { Newspaper, Gauge } from "lucide-react";
import { TV_BLUE } from "@/components/broadcast/TvFrame";
import { BACKEND_URL } from "@/lib/api";
import { IMPACT_ICONS } from "@/components/WeatherImpact";
import { BrightStage, Pill, SITE_DOMAIN } from "@/components/broadcast/shared";

function ForecastVideoSlide({ videos = [] }) {
  const [i, setI] = useState(0);
  const [ready, setReady] = useState(false);
  const vidRef = useRef(null);
  const isEncoder = typeof document !== "undefined" && document.documentElement.classList.contains("encoder-lite");
  useEffect(() => { setI(0); }, [videos.length]);
  const v = videos[Math.min(i, Math.max(videos.length - 1, 0))];
  const vId = v && v.id;
  useEffect(() => { setReady(false); vidRef.current?.play?.().catch(() => {}); }, [i, vId]);
  if (!v) return null;
  return (
    <div data-testid="forecast-video-slide" className="relative h-full w-full overflow-hidden bg-black">
      <video
        ref={vidRef}
        key={v.id}
        src={`${BACKEND_URL}${v.url}`}
        autoPlay
        playsInline
        preload="auto"
        muted={!isEncoder}
        onPlaying={() => setReady(true)}
        onError={() => setI((n) => (n + 1 < videos.length ? n + 1 : n))}
        onEnded={() => setI((n) => (n + 1 < videos.length ? n + 1 : n))}
        className="h-full w-full object-contain"
      />
      {!ready && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[#0b1220]" data-testid="forecast-video-loading">
          <div className="h-14 w-14 animate-spin rounded-full border-4 border-white/15 border-t-[#ffd60a]" />
          <div className="font-oswald text-[28px] font-bold uppercase tracking-widest text-white/80">Loading forecast…</div>
        </div>
      )}
      <div className="pointer-events-none absolute bottom-[60px] left-6 rounded-[4px] bg-black/70 px-5 py-2 font-oswald text-[26px] font-black uppercase tracking-wide text-white backdrop-blur-sm" data-testid="forecast-video-title">{v.title}</div>
      {videos.length > 1 && (
        <div className="pointer-events-none absolute bottom-[60px] right-6 rounded-[4px] bg-black/70 px-3 py-1 font-oswald text-[16px] font-bold uppercase tracking-widest text-white/80" data-testid="forecast-video-counter">{i + 1} / {videos.length}</div>
      )}
    </div>
  );
}
function BumperSlide({ videoUrl }) {
  const isMp4 = /\.(mp4|webm|mov|m4v)(\?|$)/i.test(videoUrl || "");
  const isYouTube = /youtu\.?be/i.test(videoUrl || "");
  return (
    <div className="relative flex h-full w-full items-center justify-center overflow-hidden bg-black" data-testid="broadcast-bumper">
      {isMp4 ? (
        <video
          key={videoUrl}
          src={videoUrl}
          autoPlay
          muted
          playsInline
          loop
          className="h-full w-full object-cover"
        />
      ) : isYouTube ? (
        <iframe
          key={videoUrl}
          title="On-Air Bumper"
          src={`${videoUrl.replace("watch?v=", "embed/")}?autoplay=1&mute=1&controls=0&modestbranding=1&playsinline=1&rel=0&loop=1`}
          className="h-full w-full"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          frameBorder="0"
        />
      ) : videoUrl ? (
        <video
          key={videoUrl}
          src={videoUrl}
          autoPlay
          muted
          playsInline
          loop
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center bg-gradient-to-br from-[#1a0004] via-black to-[#0a0a0a]">
          <div className="font-oswald text-xs uppercase tracking-[0.4em] text-[#d90429]">On-Air Bumper</div>
          <div className="mt-2 font-oswald text-4xl font-bold uppercase tracking-wider text-white">
            Georgia Weather Network
          </div>
          <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-gray-400">
            Add a bumper video in the Admin panel
          </div>
        </div>
      )}
    </div>
  );
}
// SponsorSlide — cycles through enabled sponsors deterministically on each render.
// A single "sponsor" catalog slot rotates who's shown; time-based rotation means
// consecutive appearances in the loop show different sponsors when >1 exist.
function SponsorSlide({ sponsors = [], durationMs = 8000 }) {
  if (!sponsors.length) return null;
  const bucket = Math.floor(Date.now() / durationMs);
  const s = sponsors[bucket % sponsors.length];
  return (
    <div
      data-testid="broadcast-sponsor"
      className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden atmo-slide atmo-dusk"
    >
      <div className="absolute inset-0 stripe-red opacity-[0.06]" />
      <div className="relative flex max-w-3xl flex-col items-center px-8 text-center">
        <div className="font-oswald text-[11px] font-bold uppercase tracking-[0.5em] text-[#ffd60a]">
          Sponsored By
        </div>
        <div className="mt-6 mb-6 flex h-40 w-full items-center justify-center lg:h-56">
          <img
            src={s.logo_url}
            alt={s.name}
            className="max-h-full max-w-full object-contain drop-shadow-[0_10px_30px_rgba(0,0,0,0.6)]"
            loading="eager"
          />
        </div>
        <div className="font-oswald text-3xl font-bold uppercase tracking-wider text-white lg:text-4xl">
          {s.name}
        </div>
        {s.tagline && (
          <div className="mt-3 max-w-xl text-sm text-gray-300 lg:text-base">
            {s.tagline}
          </div>
        )}
        {s.click_url && (
          <div className="mt-5 rounded-sm border border-white/15 bg-black/60 px-3 py-1.5 font-mono text-xs uppercase tracking-widest text-[#ffd60a]">
            {s.click_url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
          </div>
        )}
      </div>
    </div>
  );
}
function ArticleSlide({ article }) {
  const paras = (article.body || "").split(/\n\s*\n/).filter((p) => p && !p.startsWith("## ") && !/^\s*[-*]\s/.test(p));
  const excerpt = paras.slice(0, 2).join("\n\n").replace(/\*\*/g, "");
  const host = SITE_DOMAIN;
  const date = article.published_at ? new Date(article.published_at).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) : "";
  return (
    <BrightStage testid="article-slide">
      <div className="absolute inset-y-0 right-0 w-[42%]" style={{ background: "linear-gradient(160deg, #1f63c9 0%, #0a2a66 130%)", clipPath: "polygon(14% 0, 100% 0, 100% 100%, 0 100%)" }} />
      <div className="relative flex h-full w-full items-center">
        <div className="flex w-[58%] flex-col gap-6 px-[80px]">
          <Pill><Newspaper className="h-5 w-5" /> Forecast discussion · {date}</Pill>
          <div className="font-oswald text-[76px] font-black uppercase leading-[0.98] tracking-wide text-[#0a0f1c]" data-testid="article-slide-title">{article.title}</div>
          {article.subtitle && <div className="max-w-[860px] font-oswald text-[30px] leading-snug text-[#2c3542]">{article.subtitle}</div>}
          <div className="flex items-center gap-4 pt-2">
            <div className="flex h-14 w-14 items-center justify-center rounded-full font-oswald text-[20px] font-black uppercase text-white" style={{ background: "#d90429" }}>{(article.author || "").split(/\s+/).map((w) => w[0]).slice(0, 2).join("")}</div>
            <div className="font-oswald text-[24px] font-bold uppercase tracking-[0.2em] text-[#5b6675]">{article.author}</div>
            {(article.tags || []).slice(0, 3).map((t) => <span key={t} className="rounded-full border-2 px-4 py-1 font-oswald text-[18px] font-bold uppercase tracking-widest" style={{ borderColor: TV_BLUE, color: TV_BLUE }}>{t}</span>)}
          </div>
        </div>
        <div className="flex w-[42%] flex-col gap-5 py-10 pl-[120px] pr-[80px] pb-[120px]">
          <div className="overflow-hidden rounded-[6px] bg-white shadow-[0_30px_80px_rgba(10,15,28,0.3)]">
            <div className="px-7 py-3 font-oswald text-[22px] font-black uppercase tracking-[0.25em] text-white" style={{ background: "#0a0f1c" }}>From the discussion</div>
            <div className="whitespace-pre-line px-7 py-6 font-oswald text-[26px] leading-relaxed text-[#2c3542]" style={{ display: "-webkit-box", WebkitLineClamp: 12, WebkitBoxOrient: "vertical", overflow: "hidden" }} data-testid="article-slide-excerpt">{excerpt}</div>
          </div>
          <div className="rounded-[6px] bg-[#ffd60a] px-7 py-4 text-[#0a0f1c]">
            <div className="font-oswald text-[18px] font-bold uppercase tracking-[0.3em]">Read the full forecast</div>
            <div className="mt-1 break-all font-oswald text-[26px] font-black leading-tight" data-testid="article-slide-url">{host}/forecast</div>
          </div>
        </div>
      </div>
    </BrightStage>
  );
}
const IMPACT_BRIGHT = { low: "#22c55e", moderate: "#f28d1c", high: "#d90429" };
function ImpactSlide({ impact }) {
  const overall = IMPACT_BRIGHT[impact.overall] || IMPACT_BRIGHT.low;
  return (
    <BrightStage testid="impact-slide" className="p-[48px]">
      <div className="mb-5 flex items-end justify-between gap-6">
        <div>
          <Pill color="#d90429">What it means for you</Pill>
          <div className="mt-3 font-oswald text-[56px] font-black uppercase leading-none tracking-wide text-[#0a0f1c]">{impact.headline}</div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className="font-oswald text-[26px] font-bold uppercase tracking-[0.3em] text-[#5b6675]">{impact.window}</span>
          <span className="rounded-[4px] px-5 py-2 font-oswald text-[30px] font-black uppercase tracking-[0.2em] text-white" style={{ background: overall }} data-testid={`impact-level-${impact.overall}`}>{impact.overall} impact overall</span>
        </div>
      </div>
      <div className="grid h-[calc(100%-150px)] grid-cols-3 grid-rows-2 gap-4">
        {impact.categories.map((c) => {
          const Icon = IMPACT_ICONS[c.id] || Gauge;
          const col = IMPACT_BRIGHT[c.level] || IMPACT_BRIGHT.low;
          return (
            <div key={c.id} className="flex flex-col overflow-hidden rounded-[6px] bg-white shadow-[0_12px_30px_rgba(10,15,28,0.12)]" style={{ borderLeft: `10px solid ${col}` }} data-testid={`impact-card-${c.id}`}>
              <div className="flex items-center justify-between px-6 pt-5">
                <div className="flex items-center gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-[6px]" style={{ background: `${col}22` }}><Icon className="h-9 w-9" style={{ color: col }} /></div>
                  <div className="font-oswald text-[40px] font-black uppercase tracking-wide text-[#0a0f1c]">{c.label}</div>
                </div>
                <span className="rounded-full px-4 py-1 font-oswald text-[24px] font-black uppercase tracking-widest text-white" style={{ background: col }}>{c.level}</span>
              </div>
              <div className="px-6 pb-5 pt-3">
                <p className="font-oswald text-[32px] leading-snug text-[#2c3542]" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{c.summary}</p>
                {c.drivers?.length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {c.drivers.slice(0, 2).map((d) => (
                      <li key={d} className="flex items-start gap-3 font-oswald text-[26px] leading-snug text-[#5b6675]"><span className="mt-2.5 h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ background: col }} />{d}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </BrightStage>
  );
}

export { ForecastVideoSlide, BumperSlide, SponsorSlide, ArticleSlide, ImpactSlide };
